import React, { useState, useEffect, useRef } from 'react';
import { getFirestore, collection, addDoc, serverTimestamp, doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { app } from '../firebase';
import { useAuth, useEditMode } from '../AuthContext';
import { useNavigate } from 'react-router-dom';

type RaidNight = 'wednesday' | 'thursday' | 'sunday';

function toSpecIconPath(className: string, spec: string, ext: string = 'png') {
  return `/images/spec/${className.toLowerCase().replace(/ |-/g, '')}/${spec.toLowerCase().replace(/ |-/g, '')}.${ext}`;
}
const SpecIcon: React.FC<{ className: string; spec: string }> = ({ className, spec }) => {
  const [imgSrc, setImgSrc] = React.useState(toSpecIconPath(className, spec, 'webp'));
  return (
    <img
      src={imgSrc}
      alt={`${spec} icon`}
      className="w-5 h-5 inline-block"
      onError={e => {
        if (imgSrc.endsWith('.webp')) {
          setImgSrc(toSpecIconPath(className, spec, 'png'));
        } else if (imgSrc.endsWith('.png')) {
          setImgSrc(toSpecIconPath(className, spec, 'jpg'));
        } else {
          (e.target as HTMLImageElement).style.display = 'none';
        }
      }}
    />
  );
};

const Recruitment: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    name: '',
    charClass: '',
    spec: '',
    contact: '',
    discordUsername: '',
    experience: '',
    message: '',
    raidNights: {
      wednesday: { apply: false, available: '' },
      thursday: { apply: false, available: '' },
      sunday: { apply: false, available: '' },
    },
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Recruitment needs state
  const [positions, setPositions] = useState<any[]>([]);
  const [positionsLoading, setPositionsLoading] = useState(true);
  const { editMode } = useEditMode();

  // Initial hardcoded data for migration
  const initialPositions = [
    { class: 'Death Knight', spec: 'All Specs', priority: 'Low', role: 'Tank/DPS' },
    { class: 'Demon Hunter', spec: 'Havoc', priority: 'High', role: 'DPS' },
    { class: 'Demon Hunter', spec: 'Vengeance', priority: 'Low', role: 'Tank' },
    { class: 'Demon Hunter', spec: 'Devourer', priority: 'High', role: 'DPS' },
    { class: 'Druid', spec: 'All Specs', priority: 'Low', role: 'Tank/Healer/DPS' },
    { class: 'Priest', spec: 'Holy', priority: 'High', role: 'Healer' },
    { class: 'Warlock', spec: 'All Specs', priority: 'Mid', role: 'DPS' },
    { class: 'Shaman', spec: 'Elemental', priority: 'High', role: 'DPS' },
    { class: 'Shaman', spec: 'Restoration', priority: 'High', role: 'Healer' },
  ];

  useEffect(() => {
    const fetchPositions = async () => {
      setPositionsLoading(true);
      const db = getFirestore(app);
      const docRef = doc(db, 'recruitment', 'openings');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setPositions(docSnap.data().positions || []);
      } else {
        // Import initial data if Firestore is empty
        await setDoc(docRef, { positions: initialPositions });
        setPositions(initialPositions);
      }
      setPositionsLoading(false);
    };
    fetchPositions();
  }, []);

  // Admin edit UI handlers
  const handlePositionChange = (idx: number, field: string, value: string) => {
    setPositions(positions => positions.map((p, i) => i === idx ? { ...p, [field]: value } : p));
  };
  const handleAddPosition = () => {
    setPositions([...positions, { class: '', spec: '', priority: 'Low', role: '' }]);
  };
  const handleRemovePosition = (idx: number) => {
    setPositions(positions => positions.filter((_, i) => i !== idx));
  };
  const handleSavePositions = async () => {
    const db = getFirestore(app);
    const docRef = doc(db, 'recruitment', 'openings');
    await updateDoc(docRef, { positions });
    // setEditMode(false); // This line is removed as per the edit hint
  };

  const db = getFirestore(app);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleRaidNightChange = (night: RaidNight, field: 'apply' | 'available', value: any) => {
    setForm((prev) => ({
      ...prev,
      raidNights: {
        ...prev.raidNights,
        [night]: {
          ...prev.raidNights[night],
          [field]: value,
        },
      },
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Basic validation
    if (!form.name || !form.charClass || !form.spec || !form.contact || !form.discordUsername.trim() || !form.experience) {
      setError('Please fill in all required fields.');
      return;
    }
    const atLeastOneRaid = Object.values(form.raidNights).some(night => night.apply);
    if (!atLeastOneRaid) {
      setError('Please select at least one raid night you are applying for.');
      return;
    }
    if (Object.entries(form.raidNights).some(([, data]) => data.apply && !data.available)) {
      setError('Please indicate your availability for each selected raid night.');
      return;
    }
    setError('');
    // Store application in Firestore
    try {
      await addDoc(collection(db, 'applications'), {
        ...form,
        submitted: serverTimestamp(),
      });
      setSubmitted(true);
    } catch (err) {
      setError('Failed to submit application.');
    }
  };

  // Class/spec options (reuse from application form)
  const wowClasses: Record<string, string[]> = {
    'Death Knight': ['Blood', 'Frost', 'Unholy'],
    'Demon Hunter': ['Havoc', 'Vengeance', 'Devourer'],
    'Druid': ['Balance', 'Feral', 'Guardian', 'Restoration'],
    'Evoker': ['Augmentation', 'Devastation', 'Preservation'],
    'Hunter': ['Beast Mastery', 'Marksmanship', 'Survival'],
    'Mage': ['Arcane', 'Fire', 'Frost'],
    'Monk': ['Brewmaster', 'Mistweaver', 'Windwalker'],
    'Paladin': ['Holy', 'Protection', 'Retribution'],
    'Priest': ['Discipline', 'Holy', 'Shadow'],
    'Rogue': ['Assassination', 'Outlaw', 'Subtlety'],
    'Shaman': ['Elemental', 'Enhancement', 'Restoration'],
    'Warlock': ['Affliction', 'Demonology', 'Destruction'],
    'Warrior': ['Arms', 'Fury', 'Protection'],
  };
  const classOptions = Object.keys(wowClasses);
  const specOptions = form.charClass ? wowClasses[form.charClass] : [];

  const handleApplyClick = () => {
    if (!user) {
      navigate('/register');
      return;
    }
    setShowModal(true);
  };

  const [specDropdownOpenIdx, setSpecDropdownOpenIdx] = useState<number | null>(null);
  const specDropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (specDropdownOpenIdx === null) return;
    function handleClickOutside(event: MouseEvent) {
      if (specDropdownRef.current && !specDropdownRef.current.contains(event.target as Node)) {
        setSpecDropdownOpenIdx(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [specDropdownOpenIdx]);

  return (
    <div className="page-container">
      <div className="max-w-7xl mx-auto px-4 pb-12">
        <h1 className="text-4xl md:text-5xl font-bold mb-8 text-center">
          <span className="gradient-text">Recruitment</span>
        </h1>
        <div className="max-w-4xl mx-auto">
          {positionsLoading ? (
            <div className="glass p-8 rounded-xl mb-12">Loading recruitment needs...</div>
          ) : (
            editMode && user?.role === 'admin' ? (
              <div className="glass p-8 rounded-xl mb-12">
                <h2 className="text-2xl font-semibold mb-6">Edit Recruitment Needs</h2>
                {positions.map((pos, idx) => {
                  const roleOptions = [
                    'Tank',
                    'Healer',
                    'DPS',
                    'Tank/DPS',
                    'Tank/Healer',
                    'Healer/DPS',
                    'Tank/Healer/DPS',
                  ];
                  const selectedSpecs = typeof pos.spec === 'string' ? (pos.spec === 'All Specs' ? ['All Specs'] : pos.spec ? pos.spec.split(',') : []) : Array.isArray(pos.spec) ? pos.spec : [];
                  const specsForClass = pos.class ? wowClasses[pos.class] : [];
                  const handleSpecChange = (spec: string, checked: boolean) => {
                    let newSpecs: string[] = [];
                    if (spec === 'All Specs') {
                      newSpecs = checked ? ['All Specs'] : [];
                    } else {
                      newSpecs = selectedSpecs.filter((s: string) => s !== 'All Specs');
                      if (checked) {
                        newSpecs = [...newSpecs, spec];
                        // If all specs for the class are now checked, convert to 'All Specs'
                        if (specsForClass.length > 0 && newSpecs.length === specsForClass.length) {
                          newSpecs = ['All Specs'];
                        }
                      } else {
                        newSpecs = newSpecs.filter((s: string) => s !== spec);
                      }
                    }
                    handlePositionChange(idx, 'spec', newSpecs.length === 0 ? '' : newSpecs.join(','));
                  };
                  let specSummary = 'Select Spec(s)';
                  if (selectedSpecs.includes('All Specs')) {
                    specSummary = 'All Specs';
                  } else if (selectedSpecs.length === 1) {
                    specSummary = selectedSpecs[0];
                  } else if (selectedSpecs.length === 2) {
                    specSummary = selectedSpecs.join(', ');
                  } else if (selectedSpecs.length > 2) {
                    specSummary = `${selectedSpecs.length} specs selected`;
                  }
                  return (
                    <div
                      key={idx}
                      className="grid grid-cols-5 gap-2 mb-2 items-center"
                      style={{ gridTemplateColumns: 'minmax(120px,1fr) minmax(220px,2fr) minmax(100px,1fr) minmax(150px,1fr) auto' }}
                    >
                      <select
                        value={pos.class}
                        onChange={e => handlePositionChange(idx, 'class', e.target.value)}
                        className="p-2 rounded bg-surface-light border border-cyan-400 text-white min-w-[120px]"
                      >
                        <option value="">Class</option>
                        {classOptions.map(cls => (
                          <option key={cls} value={cls}>{cls}</option>
                        ))}
                      </select>
                      <div className="relative min-w-[220px]">
                        <button
                          type="button"
                          className="w-full p-2 rounded bg-surface-light border border-cyan-400 text-white text-left flex justify-between items-center"
                          onClick={() => setSpecDropdownOpenIdx(specDropdownOpenIdx === idx ? null : idx)}
                          disabled={!pos.class}
                        >
                          <span>{specSummary}</span>
                          <span className="ml-2">▼</span>
                        </button>
                        {specDropdownOpenIdx === idx && (
                          <div ref={specDropdownRef} className="absolute z-10 left-0 mt-1 w-full bg-surface-light border border-cyan-400 rounded shadow-lg p-2 flex flex-col gap-1 max-h-48 overflow-y-auto">
                            <label className="flex items-center gap-1">
                              <input
                                type="checkbox"
                                checked={selectedSpecs.includes('All Specs') || (specsForClass.length > 0 && selectedSpecs.length === specsForClass.length)}
                                onChange={e => handleSpecChange('All Specs', e.target.checked)}
                                disabled={!pos.class}
                              />
                              All Specs
                            </label>
                            {specsForClass.map(spec => (
                              <label key={spec} className="flex items-center gap-1">
                                <input
                                  type="checkbox"
                                  checked={selectedSpecs.includes('All Specs') ? false : selectedSpecs.includes(spec)}
                                  onChange={e => handleSpecChange(spec, e.target.checked)}
                                  disabled={!pos.class || selectedSpecs.includes('All Specs')}
                                />
                                {spec}
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                      <select
                        value={pos.priority}
                        onChange={e => handlePositionChange(idx, 'priority', e.target.value)}
                        className="p-2 rounded bg-surface-light border border-cyan-400 text-white min-w-[100px]"
                      >
                        <option value="High">High</option>
                        <option value="Medium">Medium</option>
                        <option value="Low">Low</option>
                      </select>
                      <select
                        value={pos.role}
                        onChange={e => handlePositionChange(idx, 'role', e.target.value)}
                        className="p-2 rounded bg-surface-light border border-cyan-400 text-white min-w-[120px]"
                      >
                        <option value="">Role</option>
                        {roleOptions.map(role => (
                          <option key={role} value={role}>{role}</option>
                        ))}
                      </select>
                      <button onClick={() => handleRemovePosition(idx)} className="text-red-400 hover:text-red-600 ml-2">Remove</button>
                    </div>
                  );
                })}
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={handleAddPosition}
                    className="inline-flex items-center justify-center px-6 py-3 glass-enhanced text-text-primary font-semibold rounded-lg hover:bg-surface-light transition-all duration-200"
                  >
                    Add Position
                  </button>
                  <button
                    onClick={handleSavePositions}
                    className="inline-flex items-center justify-center px-6 py-3 glass-enhanced text-text-primary font-semibold rounded-lg hover:bg-surface-light transition-all duration-200"
                  >
                    Save
                  </button>
                </div>
              </div>
            ) : (
              <div className="glass p-8 rounded-xl mb-12">
                <h2 className="text-2xl font-semibold mb-6">Open Positions</h2>
                
                {/* Desktop Table View */}
                <div className="hidden md:block">
                  <table className="min-w-full rounded-xl overflow-hidden">
                    <thead>
                      <tr>
                        <th className="px-4 py-2 text-left">Class</th>
                        <th className="px-4 py-2 text-left">Spec</th>
                        <th className="px-4 py-2 text-left">Priority</th>
                        <th className="px-4 py-2 text-left">Role</th>
                      </tr>
                    </thead>
                    <tbody>
                      {positions.map((pos, idx) => (
                        <tr key={idx} className="border-b border-cyan-900/30 hover:bg-surface-light transition">
                          <td className="px-4 py-2">{pos.class}</td>
                          <td className="px-4 py-2">
                            {/* Show icons for each selected spec, or all if 'All Specs' */}
                            {(() => {
                              let specs: string[] = [];
                              if (typeof pos.spec === 'string') {
                                if (pos.spec === 'All Specs') {
                                  specs = wowClasses[pos.class] || [];
                                } else if (pos.spec) {
                                  specs = pos.spec.split(',');
                                }
                              } else if (Array.isArray(pos.spec)) {
                                specs = pos.spec;
                              }
                              return (
                                <>
                                  {specs.map((spec, i) => (
                                    <span key={spec} className="inline-flex items-center mr-2">
                                      <SpecIcon className={pos.class} spec={spec} />
                                      <span className="ml-1">{spec}{i < specs.length - 1 ? ', ' : ''}</span>
                                    </span>
                                  ))}
                                </>
                              );
                            })()}
                          </td>
                          <td className="px-4 py-2">
                            <span className={`font-semibold ${pos.priority === 'High' ? 'text-red-400' : pos.priority === 'Mid' ? 'text-yellow-400' : pos.priority === 'Low' ? 'text-green-400' : 'text-gray-400'}`}>{pos.priority}</span>
                          </td>
                          <td className="px-4 py-2">{pos.role}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden space-y-4">
                  {positions.map((pos, idx) => {
                    let specs: string[] = [];
                    if (typeof pos.spec === 'string') {
                      if (pos.spec === 'All Specs') {
                        specs = wowClasses[pos.class] || [];
                      } else if (pos.spec) {
                        specs = pos.spec.split(',');
                      }
                    } else if (Array.isArray(pos.spec)) {
                      specs = pos.spec;
                    }
                    
                    return (
                      <div key={idx} className="bg-surface-light/50 p-4 rounded-lg border border-cyan-900/30">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-lg font-semibold text-cyan-400">{pos.class}</h3>
                          <span className={`px-2 py-1 rounded text-sm font-semibold ${
                            pos.priority === 'High' ? 'bg-red-900/30 text-red-400' : 
                            pos.priority === 'Mid' ? 'bg-yellow-900/30 text-yellow-400' : 
                            pos.priority === 'Low' ? 'bg-green-900/30 text-green-400' : 
                            'bg-gray-900/30 text-gray-400'
                          }`}>
                            {pos.priority}
                          </span>
                        </div>
                        
                        <div className="space-y-2">
                          <div>
                            <span className="text-sm text-gray-400">Specs: </span>
                            <div className="flex flex-wrap items-center gap-2 mt-1">
                              {specs.map((spec) => (
                                <span key={spec} className="inline-flex items-center">
                                  <SpecIcon className={pos.class} spec={spec} />
                                  <span className="ml-1 text-sm">{spec}</span>
                                </span>
                              ))}
                            </div>
                          </div>
                          
                          <div>
                            <span className="text-sm text-gray-400">Role: </span>
                            <span className="text-sm">{pos.role}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )
          )}
        </div>

        <div className="glass p-8 rounded-xl w-full max-w-2xl mx-auto">
          <h2 className="text-2xl font-semibold mb-6">Requirements</h2>
          <ul className="text-text-secondary space-y-2 mb-8">
            <li>• Looking to raid consistently and push deeper into Mythic</li>
            <li>• Enjoy progressing bosses even when the pull count starts to stack up</li>
            <li>• Enjoy putting in time in M+ to gear up</li>
            <li>• Don't expect all upgrades to come from raid</li>
            <li>• Positive attitude and team player</li>
          </ul>

          <h2 className="text-2xl font-semibold mb-6">Raid Times</h2>
          <p className="text-text-secondary mb-6">
            Wednesday and Thursday from 7:30 - 10:30 ET
          </p>

          {/* Removed 'How to Apply' section as the application form is now functional */}
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              className="inline-flex items-center justify-center px-6 py-3 btn-neon rounded-lg hover:shadow-lg transition-all duration-200"
              onClick={handleApplyClick}
            >
              Apply Now
            </button>
          </div>
          {/* Application Modal */}
          {showModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
              <div className="bg-surface p-8 rounded-xl max-w-lg w-full relative">
                <button
                  className="absolute top-4 right-4 text-gray-400 hover:text-white text-2xl"
                  onClick={() => { setShowModal(false); setSubmitted(false); setForm({ name: '', charClass: '', spec: '', contact: '', discordUsername: '', experience: '', message: '', raidNights: { wednesday: { apply: false, available: '' }, thursday: { apply: false, available: '' }, sunday: { apply: false, available: '' } } }); }}
                >
                  &times;
                </button>
                <h2 className="text-2xl font-bold mb-4 text-center">Guild Application</h2>
                {submitted ? (
                  <div className="text-center text-green-400 font-semibold py-8">
                    Thank you for applying! We will review your application and get in touch soon.
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {error && <div className="text-red-400 text-center mb-2">{error}</div>}
                    <div>
                      <label className="block mb-1 font-medium">Character Name<span className="text-red-400">*</span></label>
                      <input type="text" name="name" value={form.name} onChange={handleChange} className="w-full p-2 rounded bg-surface-light border border-border focus:outline-none focus:ring-2 focus:ring-cyan-400" required />
                    </div>
                    <div>
                      <label className="block mb-1 font-medium">Class<span className="text-red-400">*</span></label>
                      <select
                        name="charClass"
                        value={form.charClass}
                        onChange={handleChange}
                        className="w-full p-2 rounded bg-surface-light border border-border focus:outline-none focus:ring-2 focus:ring-cyan-400"
                        required
                      >
                        <option value="">Select a class</option>
                        {classOptions.map((cls) => (
                          <option key={cls} value={cls}>{cls}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block mb-1 font-medium">Spec<span className="text-red-400">*</span></label>
                      <select
                        name="spec"
                        value={form.spec}
                        onChange={handleChange}
                        className="w-full p-2 rounded bg-surface-light border border-border focus:outline-none focus:ring-2 focus:ring-cyan-400"
                        required
                        disabled={!form.charClass}
                      >
                        <option value="">{form.charClass ? 'Select a spec' : 'Select a class first'}</option>
                        {specOptions.map((spec) => (
                          <option key={spec} value={spec}>{spec}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block mb-1 font-medium">BattleTag<span className="text-red-400">*</span></label>
                      <input type="text" name="contact" value={form.contact} onChange={handleChange} className="w-full p-2 rounded bg-surface-light border border-border focus:outline-none focus:ring-2 focus:ring-cyan-400" required />
                    </div>
                    <div>
                      <label className="block mb-1 font-medium">Discord username<span className="text-red-400">*</span></label>
                      <input
                        type="text"
                        name="discordUsername"
                        value={form.discordUsername}
                        onChange={handleChange}
                        autoComplete="username"
                        placeholder="e.g. username or username#0000"
                        className="w-full p-2 rounded bg-surface-light border border-border focus:outline-none focus:ring-2 focus:ring-cyan-400"
                        required
                      />
                    </div>
                    <div>
                      <label className="block mb-1 font-medium">Raiding Experience<span className="text-red-400">*</span></label>
                      <textarea name="experience" value={form.experience} onChange={handleChange} className="w-full p-2 rounded bg-surface-light border border-border focus:outline-none focus:ring-2 focus:ring-cyan-400" rows={2} required />
                    </div>
                    <div>
                      <label className="block mb-1 font-medium">Raid Nights<span className="text-red-400">*</span></label>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="wednesday"
                            checked={form.raidNights.wednesday.apply}
                            onChange={e => handleRaidNightChange('wednesday', 'apply', e.target.checked)}
                          />
                          <label htmlFor="wednesday" className="font-medium">Wednesday (Mythic)</label>
                          {form.raidNights.wednesday.apply && (
                            <select
                              className="ml-2 p-1 rounded border border-border bg-surface-light"
                              value={form.raidNights.wednesday.available}
                              onChange={e => handleRaidNightChange('wednesday', 'available', e.target.value)}
                              required
                            >
                              <option value="">Available?</option>
                              <option value="yes">Yes</option>
                              <option value="no">No</option>
                            </select>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="thursday"
                            checked={form.raidNights.thursday.apply}
                            onChange={e => handleRaidNightChange('thursday', 'apply', e.target.checked)}
                          />
                          <label htmlFor="thursday" className="font-medium">Thursday (Mythic)</label>
                          {form.raidNights.thursday.apply && (
                            <select
                              className="ml-2 p-1 rounded border border-border bg-surface-light"
                              value={form.raidNights.thursday.available}
                              onChange={e => handleRaidNightChange('thursday', 'available', e.target.value)}
                              required
                            >
                              <option value="">Available?</option>
                              <option value="yes">Yes</option>
                              <option value="no">No</option>
                            </select>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="sunday"
                            checked={form.raidNights.sunday.apply}
                            onChange={e => handleRaidNightChange('sunday', 'apply', e.target.checked)}
                          />
                          <label htmlFor="sunday" className="font-medium">Sunday (Heroic/AOTC)</label>
                          {form.raidNights.sunday.apply && (
                            <select
                              className="ml-2 p-1 rounded border border-border bg-surface-light"
                              value={form.raidNights.sunday.available}
                              onChange={e => handleRaidNightChange('sunday', 'available', e.target.value)}
                              required
                            >
                              <option value="">Available?</option>
                              <option value="yes">Yes</option>
                              <option value="no">No</option>
                            </select>
                          )}
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="block mb-1 font-medium">Message</label>
                      <textarea name="message" value={form.message} onChange={handleChange} className="w-full p-2 rounded bg-surface-light border border-border focus:outline-none focus:ring-2 focus:ring-cyan-400" rows={2} />
                    </div>
                    <button type="submit" className="w-full btn-neon py-2 rounded font-bold mt-2">Submit Application</button>
                  </form>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Recruitment; 